import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Col, Row, Button, Divider, Modal, Form, Select, Input, notification,
} from 'antd';
import PropTypes from 'prop-types';
import { backendResponseCodes } from '../../../../../utils/responseMessages';
import {
  isSuperAgentError, post, RequestWrapper, ResponseWrapper,
} from '../../../../../dataSource/requests';
import { getUserType } from '../../../../../utils/users';
import './EstimateDetailFields.scss';
import '../../ServiceDetail.scss';

const EstimateDetailFields = (props): JSX.Element => {
  const { data, loadData } = props;
  const navigate = useNavigate();
  const [formInstance] = Form.useForm();

  const reasonInputRef = useRef<Input | undefined>(undefined);

  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [visibleApproverModal, setVisibleApproverModal] = useState<boolean>(false);
  const options = [
    'Forma de Pagamento',
    'Não estou de acordo com o Valor',
    'Serviço já realizado',
    'Tempo de resposta',
    'Não irei realizar o serviço no momento',
    'Valores do concorrente melhor',
    'Orçamento diferente do desejado',
    'Mal atendido',
    'Outros',
  ];
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [triggerOperationBranch, setTriggerOperationBranch] = useState<boolean>(false);

  const selectField = (value: string): JSX.Element | null => {
    if (value === 'Outros') {
      return (
        <Form.Item
          name="reason"
          rules={[
            {
              required: true,
              message: 'Por favor informe o motivo.',
            },
          ]}
          label="Motivo"
        >
          <Input
            id="reasonInput"
            ref={reasonInputRef}
          />
        </Form.Item>
      );
    }

    return null;
  };

  const approverOrRefuse = async (typeAction: string): Promise<void> => {
    setConfirmLoading(true);

    const targetUrl = '/budget/approveOrRefuseBudget';
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    let sendData = {
      budgetId: data.id,
      approvedBudget: true,
      descriptionRefuse: '',
      reasonRefuses: '',
    };

    if (typeAction === 'Refuse') {
      sendData = {
        budgetId: data.id,
        approvedBudget: false,
        // descriptionRefuse: (!document.getElementById('reasonInput')
        //   ? ''
        //   : document.getElementById('reasonInput')?.value),
        descriptionRefuse: (!reasonInputRef.current ? '' : reasonInputRef?.current?.value),
        reasonRefuses: (formInstance.getFieldValue('option') === undefined ? '' : formInstance.getFieldValue('option')),
      };
    }

    console.log(sendData);

    const requestReference: RequestWrapper = post({
      url: targetUrl,
      params: sendData,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (responseError?.body.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden
            .description.pt_br,
        });
        break;

      case (responseError?.body.code === -14003):
        // "Budget not found", -14003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget
            .BudgetNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body.code === -14006):
        // "Description refuse required", -14006
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget
            .DescriptionRefuseRequired
            .description.pt_br,
        });
        break;

      case (responseError?.body.code === -14007):
        // "Budget already rejected", -14007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget.BudgetAlreadyRejected
            .description.pt_br,
        });
        break;

      case (responseError?.body.code === -14008):
        // "Budget already approved", -14008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget.BudgetAlreadyApproved
            .description.pt_br,
        });
        break;

      case (response?.body?.code === 13004):
        // "Budget recused got successfully", 13004
        notification.success({
          message: 'Recusado',
          description: 'O orçamento foi recusado com sucesso!',
        });

        setVisibleModal(false);
        setTriggerOperationBranch(true);
        loadData(data.id);
        break;

      case (response?.body?.code === 13005):
        // "Budget approved got successfully", 13005
        notification.success({
          message: 'Aprovado',
          description: 'O orçamento foi aprovado com sucesso!',
        });

        setVisibleApproverModal(false);
        setTriggerOperationBranch(true);
        loadData(data.id);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao concluir orçamento, por favor tente novamente mais tarde.',
        });
        break;
    }

    setConfirmLoading(false);
  };

  useEffect(() => {
    getUserType();

    return () => {
      setConfirmLoading(false);
    };
  }, []);

  const renderEstimateDetailFields = (): JSX.Element => {
    return (
      <div>
        <Row>
          <Col xs={{ span: 12 }} lg={{ span: 12 }}>
            <p className="item-title">Orçamento: </p>
          </Col>
        </Row>
        <Row justify="space-between" className="param-row">
          <Col>Valor unitário:</Col>
          <Col><b>{`${data.unitValue}`}</b></Col>
        </Row>
        <Row justify="space-between" className="param-row">
          <Col>Valor total:</Col>
          <Col><b>{`${data.totalValue}`}</b></Col>
        </Row>
        <Row justify="space-between" className="param-row">
          <Col>Condição de pagamento:</Col>
          <Col><b>{data.paymentCondition}</b></Col>
        </Row>
        <Row justify="space-between" className="param-row">
          <Col>Forma de pagamento:</Col>
          <Col><b>{data.paymentForm}</b></Col>
        </Row>
        <Row className="param-row">
          <p className="item-title">Observação:</p>
        </Row>
        <Row gutter={24} className="inline-field param-row">
          <Col span={24}>
            <Col className="observacaoCol">{data.estimateObs}</Col>
          </Col>
        </Row>

        <Row className="param-row">
          <p className="item-title">Responsável pelo orçamento:</p>
        </Row>
        <Row gutter={24} className="inline-field param-row">
          <Col span={24}>
            <Col className="observacaoCol">
              {
                !data.responsableBudget
                  ? '-'
                  : data.responsableBudget
              }
            </Col>
          </Col>
        </Row>

        <Row className="param-row">
          <p className="item-title">
            {
              (
                data.status === 'Orçamento recusado'
                  ? 'Data/hora da recusa:'
                  : data.status === 'Orçamento expirado'
                    ? 'Data/hora da expiração:'
                    : data.status === 'Orçamento aprovado'
                      ? 'Data/hora da aprovação:'
                      : 'Data/hora:'
              )
            }
          </p>
        </Row>
        <Row gutter={24} className="inline-field param-row">
          <Col span={24}>
            <Col className="observacaoCol">
              {
                data.status === 'Orçamento expirado'
                  ? (data?.budgetExpiredDate)
                  : data.approveOrRecuseDate
              }
            </Col>
          </Col>
        </Row>

        {
          (data.reasonRefuses !== null && data.reasonRefuses !== '' && data.status !== 'Orçamento aprovado')
            ? (
              <>
                <Row className="param-row">
                  <p className="item-title">Motivo da recusa:</p>
                </Row>
                <Row gutter={24} className="inline-field param-row">
                  <Col span={24}>
                    <Col className="observacaoCol">
                      {data.reasonRefuses}
                    </Col>
                  </Col>
                </Row>
              </>
            )
            : ''
        }

        {
          (data.descriptionRefuse !== null && data.descriptionRefuse !== '' && data.status !== 'Orçamento aprovado')
            ? (
              <>
                <Row className="param-row">
                  <p className="item-title">Descrição da recusa:</p>
                </Row>
                <Row gutter={24} className="inline-field param-row">
                  <Col span={24}>
                    <Col className="observacaoCol">
                      {data.descriptionRefuse}
                    </Col>
                  </Col>
                </Row>
              </>
            )
            : ''
        }

        {
          getUserType() === 'COOPERATIVE'
            && data.status === 'Aguardando aceite do orçamento'
            && triggerOperationBranch === false
            ? (
              <Row>
                <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                  <div className="buttons-container">
                    <Row gutter={[24, 24]}>
                      <Col
                        xs={{ span: 24 }}
                        md={{ span: 8 }}
                      >
                        <Button
                          className="btn-previous-estimate"
                          onClick={
                            (): void => {
                              setVisibleModal(true);
                            }
                          }
                        >
                          Recusar
                        </Button>
                      </Col>
                      <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Button
                          className="btn-edit"
                          type="primary"
                          onClick={
                            (): void => {
                              setVisibleApproverModal(true);
                            }
                          }
                        >
                          Aprovar
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            )
            : ''
        }

        <Divider type="horizontal" />

        <Modal
          className="recusa-modal"
          title={<h3 style={{ margin: 0 }}>Motivo da recusa</h3>}
          visible={visibleModal}
          onCancel={
            (): void => {
              setVisibleModal(false);
            }
          }
          footer={[
            <Button
              className="btn-previous-estimate"
              onClick={
                (): void => {
                  setVisibleModal(false);
                }
              }
            >
              Cancelar
            </Button>,
            <Button
              className="btn-ok"
              type="primary"
              key="submit"
              loading={confirmLoading}
              onClick={
                async (): Promise<void> => {
                  try {
                    await formInstance.validateFields();
                    approverOrRefuse('Refuse');
                  } catch (error) {
                    console.log('validatefields fail');
                  }
                }
              }
            >
              Confirmar
            </Button>,
          ]}
        >
          <Col>
            <Row>
              Informe o motivo
            </Row>
            <Form
              scrollToFirstError
              form={formInstance}
              layout="vertical"
            >
              <Form.Item
                name="option"
                rules={[
                  { required: true, message: 'Por favor, selecione a opção' },
                ]}
              >
                <Select
                  onChange={
                    (v): void => {
                      setSelectedOption(v);
                    }
                  }
                  style={{ minWidth: 80, width: '100%' }}
                >
                  {
                    options.map((v, i) => (
                      <Select.Option
                        key={i}
                        value={v}
                      >
                        {v}
                      </Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
              {
                selectField(selectedOption)
              }
            </Form>
          </Col>
        </Modal>

        <Modal
          className="recusa-modal"
          confirmLoading={confirmLoading}
          title={(
            <h3 style={{ margin: 0 }}>
              Confirmar orçamento no valor e condições propostas?
            </h3>
          )}
          visible={visibleApproverModal}
          onCancel={
            (): void => {
              setVisibleApproverModal(false);
            }
          }
          footer={[
            <Button
              className="btn-previous-estimate"
              onClick={
                (): void => {
                  setVisibleApproverModal(false);
                }
              }
            >
              Cancelar
            </Button>,
            <Button
              className="btn-ok"
              type="primary"
              onClick={
                async (): Promise<void> => {
                  approverOrRefuse('Approver');
                }
              }
            >
              Confirmar
            </Button>,
          ]}
        >
          <p>
            Uma vez confirmado, não será possível recusar o orçamento!
          </p>
        </Modal>
      </div>
    );
  };

  return (
    <div className="EstimateDetailFields">
      {renderEstimateDetailFields()}
    </div>
  );
};

EstimateDetailFields.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  loadData: PropTypes.func.isRequired,
};

EstimateDetailFields.defaultProps = {};

export default EstimateDetailFields;
